import { useState, useEffect, useRef } from 'react'
// context
import Context from './Context'
// service
import authService from './index'
// lodash
import { get } from 'lodash'
// axios
import axios from 'axios'
// firebase
import { onIdTokenChanged } from 'firebase/auth'
import { auth } from '../firebase/app'

const BASE_PATH = process.env.REACT_APP_API_BASEURL
const USERS_ME_ENDPOINT = '/users/me'

const buildUsersMeUrl = () => {
  const normalizedBase = BASE_PATH ? BASE_PATH.replace(/\/$/, '') : ''
  return `${normalizedBase}${USERS_ME_ENDPOINT}`
}

export default function AuthProvider(attr) {
  const [value, setValue] = useState(authService.state)
  const isAuthenticated = get(value, 'user.isAuthenticated', false)
  const token = get(value, 'user.token', null)
  const lastFetchedToken = useRef(null)

  useEffect(() => {
    authService.evalLogin()
    authService.subscribe(setValue)
  }, [])

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        if (get(authService.state, 'user.isAuthenticated') || get(authService.state, 'user.token')) {
          authService.logout({ skipFirebaseSignOut: true, reload: false })
        }
        return
      }

      try {
        const refreshedToken = await firebaseUser.getIdToken()
        const currentToken = get(authService.state, 'user.token', null)

        if (refreshedToken && refreshedToken !== currentToken) {
          authService.setToken(refreshedToken)
        }
      } catch (error) {
        console.error('Error refreshing Firebase token', error)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (!isAuthenticated) {
        lastFetchedToken.current = null
      }
      return
    }

    if (lastFetchedToken.current === token) {
      return
    }

    lastFetchedToken.current = token
    authService.setGetting(true)

    const axiosConfig = {
      url: buildUsersMeUrl(),
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    }

    axios(axiosConfig)
      .then((resp) => {
        const data = get(resp, 'data', null)

        if (!data) {
          lastFetchedToken.current = null
          authService.setGetting(false)
          return
        }

        const memberships = Array.isArray(get(data, 'memberships'))
          ? get(data, 'memberships')
          : []

        const businesses = memberships
          .map((membership) => get(membership, 'business', membership))
          .filter(Boolean)

        const tenantId = memberships.find((membership) => get(membership, 'tenantId'))?.tenantId || null

        const fullName = get(data, 'profile.displayName', null)
        const username = get(data, 'profile.email', null)
        const roles = get(data, 'token.claims.roles', null)
        const permissions = get(data, 'permissions', null)
        const profileUrl =
          get(data, 'profile.avatarUrl', null) || get(data, 'profileUrl', null) || get(value, 'user.profileUrl', null)
        const status = get(data, 'status', null) || get(data, 'profile.status', null)
        const firebaseUserId = get(data, 'profile.id', null)

        authService.setLogin(
          token,
          fullName || get(value, 'user.name', username) || username,
          username,
          roles,
          permissions,
          businesses,
          profileUrl,
          status,
          firebaseUserId,
          tenantId,
        )

        axios({
          url: `${BASE_PATH}/marketplace/public/categories`,
          method: 'GET',
          headers: { Accept: 'application/json' }
        }).then((response) => authService.setCategories((response && response.data && response.data.length > 0)?response.data:[]))
      })
      .catch(() => {
        lastFetchedToken.current = null
        authService.logout()
      })
  }, [isAuthenticated, token, value])

  const props = {
    value,
    ...attr,
  }

  return <Context.Provider {...props} />
}
